import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Package, Storefront, ChatCircleDots, X, ArrowCounterClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";

const POLL_MS = 30_000; // refresca cada 30 segundos
const DISMISS_KEY = "labiferia:notifDismissed:v1";

function readDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
}
function writeDismissed(setObj) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...setObj])); } catch { /* noop */ }
}

/**
 * Campanita de notificaciones — muestra los pedidos (TN + manuales) que
 * están en estado "listo_para_facturar" y quedan pendientes de facturar.
 * Cada notificación se puede descartar con la X (se guarda en localStorage).
 */
export default function NotificationBell() {
  const [tnListos, setTnListos] = useState([]);
  const [pedidosListos, setPedidosListos] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [tn, ped] = await Promise.all([
        api.get("/tiendanube/ventas", { params: { estado: "listo_para_facturar", limit: 50 } }),
        api.get("/pedidos", { params: { estado: "listo_para_facturar", limit: 50 } }),
      ]);
      setTnListos(tn.data || []);
      setPedidosListos(ped.data || []);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Cerrar al hacer click fuera
  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const total = tnListos.length + pedidosListos.length;

  // Filtrados por descartados (dismissed persiste en localStorage)
  const tnVisible = useMemo(() => tnListos.filter((v) => !dismissed.has(`tn:${v.id}`)), [tnListos, dismissed]);
  const pedVisible = useMemo(() => pedidosListos.filter((p) => !dismissed.has(`pedido:${p.id}`)), [pedidosListos, dismissed]);
  const totalVisible = tnVisible.length + pedVisible.length;

  const dismissedCount = useMemo(() => {
    const activos = new Set([
      ...tnListos.map((v) => `tn:${v.id}`),
      ...pedidosListos.map((p) => `pedido:${p.id}`),
    ]);
    return [...dismissed].filter((k) => activos.has(k)).length;
  }, [dismissed, tnListos, pedidosListos]);

  const dismiss = (key, e) => {
    e?.stopPropagation();
    const next = new Set(dismissed);
    next.add(key);
    setDismissed(next);
    writeDismissed(next);
  };

  const restoreAll = () => {
    const next = new Set();
    setDismissed(next);
    writeDismissed(next);
  };

  const goToFacturar = (source, id) => {
    setOpen(false);
    if (source === "tn") navigate(`/ventas?tn_venta_id=${id}`);
    else navigate(`/ventas?pedido_id=${id}`);
  };

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`relative flex items-center justify-center w-8 h-8 rounded-md transition-colors ${
          totalVisible > 0
            ? "bg-purple-500/20 hover:bg-purple-500/30 text-purple-200"
            : "hover:bg-white/10 text-neutral-300"
        }`}
        title={totalVisible > 0 ? `${totalVisible} pedido${totalVisible > 1 ? "s" : ""} listo${totalVisible > 1 ? "s" : ""} para facturar` : "Sin pedidos pendientes"}
        data-testid="notif-bell-btn"
      >
        <Bell size={18} weight={totalVisible > 0 ? "fill" : "regular"} />
        {totalVisible > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1 border-2 border-neutral-900 animate-pulse"
            data-testid="notif-badge-count"
          >
            {totalVisible > 99 ? "99+" : totalVisible}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 max-h-[70vh] overflow-hidden bg-white border border-neutral-300 rounded-md shadow-xl z-50"
          data-testid="notif-panel"
        >
          <div className="px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
            <div className="flex items-center gap-2">
              <Bell size={16} weight="fill" />
              <div className="font-semibold text-sm flex-1">Pedidos listos para facturar</div>
              {dismissedCount > 0 && (
                <button
                  type="button"
                  onClick={restoreAll}
                  className="text-[11px] font-medium bg-white/10 hover:bg-white/20 rounded px-2 py-0.5 flex items-center gap-1 transition-colors"
                  title="Volver a mostrar las notificaciones descartadas"
                  data-testid="notif-restore-all"
                >
                  <ArrowCounterClockwise size={11} weight="bold" /> Restaurar {dismissedCount}
                </button>
              )}
            </div>
            <div className="text-[11px] mt-0.5 text-purple-100 font-mono">
              {totalVisible === 0 ? "Sin pendientes." : `${totalVisible} pedido${totalVisible > 1 ? "s" : ""} · producción completa`}
            </div>
          </div>

          {totalVisible === 0 ? (
            <div className="px-6 py-10 text-center text-neutral-400 text-sm">
              <Package size={28} className="mx-auto mb-2 opacity-40" />
              {dismissedCount > 0
                ? `Tenés ${dismissedCount} notificación${dismissedCount > 1 ? "es" : ""} descartada${dismissedCount > 1 ? "s" : ""}.`
                : "No hay pedidos listos para facturar."}
            </div>
          ) : (
            <div className="max-h-[55vh] overflow-y-auto divide-y divide-neutral-100">
              {tnVisible.map((v) => (
                <NotifItem
                  key={`tn-${v.id}`}
                  icon={<Storefront size={16} weight="fill" className="text-emerald-600" />}
                  origen="Tienda Nube"
                  numero={`#${v.tn_number}`}
                  cliente={v.cliente_nombre}
                  total={v.total}
                  moneda={v.moneda}
                  fechaEnvio={v.fecha_envio}
                  onClick={() => goToFacturar("tn", v.id)}
                  onDismiss={(e) => dismiss(`tn:${v.id}`, e)}
                />
              ))}
              {pedVisible.map((p) => (
                <NotifItem
                  key={`ped-${p.id}`}
                  icon={<ChatCircleDots size={16} weight="fill" className="text-blue-600" />}
                  origen="Pedido manual"
                  numero={`#${p.numero}`}
                  cliente={p.cliente_nombre}
                  total={p.total}
                  moneda={p.moneda}
                  fechaEnvio={p.fecha_envio}
                  onClick={() => goToFacturar("pedido", p.id)}
                  onDismiss={(e) => dismiss(`pedido:${p.id}`, e)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotifItem({ icon, origen, numero, cliente, total, moneda = "ARS", fechaEnvio, onClick, onDismiss }) {
  return (
    <div
      className="group relative w-full flex items-start gap-3 hover:bg-purple-50/60 transition-colors"
      data-testid={`notif-item-${numero.replace("#", "")}`}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex-1 text-left px-4 py-3 flex items-start gap-3 min-w-0"
      >
        <div className="mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-mono text-xs uppercase tracking-widest text-neutral-500">
              {origen} · <span className="font-bold text-neutral-800">{numero}</span>
            </div>
            <div className="font-mono text-sm font-bold text-neutral-800">
              {new Intl.NumberFormat("es-AR", { style: "currency", currency: moneda }).format(total || 0)}
            </div>
          </div>
          <div className="text-sm text-neutral-700 truncate mt-0.5">{cliente || "—"}</div>
          {fechaEnvio && (
            <div className="text-[11px] text-neutral-400 font-mono mt-0.5">Envío: {fechaEnvio}</div>
          )}
        </div>
        <div className="text-purple-600 text-xs font-semibold whitespace-nowrap self-center">Facturar →</div>
      </button>
      <button
        type="button"
        onClick={onDismiss}
        className="absolute top-1.5 right-1.5 w-5 h-5 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-rose-600 hover:bg-rose-50 transition-all"
        title="Descartar esta notificación"
        data-testid={`notif-dismiss-${numero.replace("#", "")}`}
      >
        <X size={12} weight="bold" />
      </button>
    </div>
  );
}
